import { isArray, omit, range, uniqBy } from "lodash";
import {
  CareerTrackRoleType,
  CompetencyAppliesTo,
  GetOrganizationCompetenciesQuery,
  OrganizationCompetencyFragment,
  SaveOrgWideCareerTrackMutationVariables,
} from "types/graphql-schema";
import { v4 as uuidv4 } from "uuid";

import { UseLabelType } from "@apps/use-label/use-label";
import { UserComboboxTeamOption } from "@components/user-combobox/user-combobox-list";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import { assertEdgesNonNull } from "@helpers/helpers";

import { CareerTrackFormRole } from "./career-track-form/career-track-form";
import { formatNameList } from "./competency-sharing";
import { CompetencyCriteriaInputWithUUID } from "./types";

export const roleLevelIterator = (levelCount: number) =>
  range(1, levelCount + 1);

export const stickyColWidth = "w-80 min-w-80 max-w-80";
export const roleColWidth = "w-92 max-w-92 min-w-92";
// Wide enough for its "Add Role for This Track" button on one line.
export const emptyRoleColWidth = "w-60 max-w-60 min-w-60";
// Trailing column in the career track editor whose header adds a level. It has
// no fixed width, so it takes up the rest of the table's width, and it carries
// the same borders as every other cell so each row's lines run the full width.
export const addLevelColClassName =
  "min-w-16 border border-r-0 bg-content-background";
export const addLevelTitleColClassName =
  "min-w-16 border border-r-0 bg-gray-50";

/**
# get_role_filter(), resolved_criteria() [from career.models.Competency on backend]
# and doesCompetencyApplyToCareerTrack() [in career-track-from on frontend] are all
# used to determine whether a competency is applicable to a career track and any changes
# to one should be reflected in the others
*/
export const doesCompetencyApplyToCareerTrack = (
  competency: OrganizationCompetencyFragment,
  roles: CareerTrackFormRole[],
  careerTrackData: Omit<SaveOrgWideCareerTrackMutationVariables, "roles">,
  careerTrackId?: number | null,
  associatedTeam?: UserComboboxTeamOption | null,
) => {
  const competencyAppliesToRoleType =
    competency.appliesToManagementRoles || competency.appliesToIcRoles;

  const competencyAppliesToMgmt =
    competency.appliesToManagementRoles &&
    (roles.length === 0 ||
      roles.some((role) => role.roleType === CareerTrackRoleType.Management));
  const competencyAppliesToIc =
    competency.appliesToIcRoles &&
    (roles.length === 0 ||
      roles.some((role) => role.roleType === CareerTrackRoleType.Ic));

  const appliesToTeamIds = assertEdgesNonNull(competency.appliesToTeams).map(
    (team) => team.id,
  );
  const appliesToCareerTrackIds = assertEdgesNonNull(
    competency.appliesToCareerTracks,
  ).map((track) => track.id);
  const hasSpecificTrackFilters =
    appliesToCareerTrackIds.length > 0 || appliesToTeamIds.length > 0;
  const competencyAppliesToTeam =
    associatedTeam && appliesToTeamIds.includes(associatedTeam.id);
  const competencyAppliesToCareerTrack =
    careerTrackId && appliesToCareerTrackIds.includes(careerTrackId);
  const competencyManuallyAdded =
    careerTrackData.additionalCompetencies &&
    isArray(careerTrackData.additionalCompetencies) &&
    careerTrackData.additionalCompetencies.includes(competency.id);

  if (
    competencyManuallyAdded ||
    competency.appliesTo === CompetencyAppliesTo.AllRoles
  ) {
    return true;
  }

  return (
    (competencyAppliesToRoleType || hasSpecificTrackFilters) &&
    (!competencyAppliesToRoleType ||
      competencyAppliesToIc ||
      competencyAppliesToMgmt) &&
    (!hasSpecificTrackFilters ||
      competencyAppliesToTeam ||
      competencyAppliesToCareerTrack)
  );
};

// Which section of a career track's table a competency belongs to: everyone,
// more than one career track, or this track alone. See
// getCompetencyScopeSection for how a competency is filed.
export type CompetencyScopeSection = "company" | "shared" | "track";

/**
 * How far a competency reaches, which is what a section of the tables stands
 * for: everyone, more than one career track, or one alone. The question a
 * section answers is "who else does editing this affect", so a competency
 * files under the broadest thing it touches rather than the most specific.
 */
export const getCompetencyScopeSection = (
  competency: Pick<
    OrganizationCompetencyFragment,
    | "appliesTo"
    | "appliesToIcRoles"
    | "appliesToManagementRoles"
    | "appliesToTeams"
    | "appliesToCareerTracks"
  >,
  // The track being looked at, when there is one. Without it the question is
  // only how many tracks a competency names.
  careerTrackId?: number | null,
): CompetencyScopeSection => {
  if (competency.appliesTo === CompetencyAppliesTo.AllRoles) {
    return "company";
  }
  const teamIds = assertEdgesNonNull(competency.appliesToTeams).map(
    (team) => team.id,
  );
  const trackIds = assertEdgesNonNull(competency.appliesToCareerTracks).map(
    (track) => track.id,
  );
  if (teamIds.length === 0 && trackIds.length === 0) {
    // A role type and nothing else reaches that type across the organization.
    // Narrowed to nothing at all, it is a leftover from before the form
    // derived this, and reads as the narrowest thing there is.
    return competency.appliesToIcRoles || competency.appliesToManagementRoles
      ? "company"
      : "track";
  }
  // A department is open-ended: whichever track is added to it next inherits
  // this competency, so editing it was never local to one track.
  if (teamIds.length > 0) {
    return "shared";
  }
  const isOnlyThisTrack =
    trackIds.length === 1 && (!careerTrackId || trackIds[0] === careerTrackId);
  return isOnlyThisTrack ? "track" : "shared";
};

// The way back to the overview, in place of tabs: "Roles and Competencies"
// alone on the overview, then the view being edited ("All Tracks", a track,
// a department or a person) as a second crumb everywhere else.
export const getRolesAndCompetenciesBreadcrumbs = (
  organizationId: number,
  label: UseLabelType,
  currentViewTitle?: string,
) => [
  {
    title: `${label("career", { capitalize: true })} Tracks`,
    url: careerTrackOverviewPath(organizationId),
  },
  ...(currentViewTitle ? [{ title: currentViewTitle }] : []),
];

// Name a role gets when nobody has typed one, e.g. "Engineering 2", or
// "Level 2" while the career track has no name yet.
export const getDefaultRoleTitle = (
  careerTrackTitle: string | null | undefined,
  level: number,
) => {
  const trackTitle = careerTrackTitle?.trim();
  return trackTitle ? `${trackTitle} ${level}` : `Level ${level}`;
};

// For delete confirmations: deleting a role takes it away from everyone who
// holds it, so say who that is.
export const describeRoleHolders = (
  people: ReadonlyArray<{ id: number; name: string }>,
  roleCount = 1,
) => {
  const holders = uniqBy(people, (person) => person.id);
  if (holders.length === 0) {
    return `Nobody holds ${roleCount === 1 ? "this role" : "these roles"} yet.`;
  }
  return `${formatNameList(holders.map((person) => person.name))} ${
    holders.length === 1 ? "loses" : "lose"
  } ${roleCount === 1 ? "it" : "their role"}.`;
};

export const careerTrackPath = (
  organizationId: number,
  careerTrackId: number,
) =>
  `/settings/organization/${organizationId}/career-track/edit/${careerTrackId}`;

export const newCareerTrackPath = (organizationId: number) =>
  `/settings/organization/${organizationId}/career-track/new`;

// Every department and career track in one table: the way in to everything
// the track editor can show.
export const careerTrackOverviewPath = (organizationId: number) =>
  `/settings/organization/${organizationId}/career-track/overview`;

// Competencies that apply to every role, edited level by level in one place.
export const allRolesPath = (organizationId: number) =>
  `/settings/organization/${organizationId}/career-track/all-roles`;

// The same table with every competency in it, whoever it applies to.
export const allCompetenciesPath = (organizationId: number) =>
  `/settings/organization/${organizationId}/career-track/all-competencies`;

// The same table for one department's competencies: the ones every track in
// it is held to.
export const departmentCompetenciesPath = (
  organizationId: number,
  teamId: number,
) =>
  `/settings/organization/${organizationId}/career-track/department/${teamId}`;

// One person's view: their role's column, plus criteria written for them.
export const personCareerPath = (organizationId: number, userId: number) =>
  `/settings/organization/${organizationId}/career-track/person/${userId}`;

// Competencies scoped to a department, the ones each of its tracks inherits.
// Company-wide ones reach it too, but are counted on their own row.
export const isDepartmentCompetency = (
  competency: Pick<OrganizationCompetencyFragment, "appliesTo"> & {
    appliesToTeams: OrganizationCompetencyFragment["appliesToTeams"];
  },
  teamId: number,
) =>
  competency.appliesTo !== CompetencyAppliesTo.AllRoles &&
  assertEdgesNonNull(competency.appliesToTeams).some(
    (team) => team.id === teamId,
  );

// The table uses collapsed borders, which don't move with sticky cells, so the
// pinned header row draws its bottom border as a shadow instead, and hides the
// real border on that edge (hidden always wins) so the line isn't doubled.
export const stickyHeaderBorderClassName =
  "shadow-[inset_0_-1px_0_#e5e7eb] [border-bottom-style:hidden]";

// The same for the pinned name column's cells: their white background paints
// over the row line beneath them, so they draw their own. In Preview, with no
// section headers between rows, nothing else marks where one row ends. The
// real border on that edge is hidden, or where it does show (above the filler
// row) the line is doubled.
export const stickyCellBottomLineClassName =
  "shadow-[inset_0_-1px_0_#e5e7eb] [border-bottom-style:hidden]";

// Every cell of a body row draws its bottom line the same way. A collapsed
// border is painted on the next row's top pixel, while the pinned name cell's
// shadow sits on its own bottom pixel, so mixing the two left the name
// column's line 1px above the rest of the row's.
export const bodyRowLineClassName =
  "[&>td]:shadow-[inset_0_-1px_0_#e5e7eb] [&>td]:[border-bottom-style:hidden]";

// The ids saves gave the items they created, by the key each was sent with
// (its clientId; see withSavedId). Filled from each save's createdIds.
export type SavedIdsByUuid = ReadonlyMap<string, number>;

// An item's id: its own, or the one a save gave it since.
const savedIdOf = (
  item: { id?: number | null; uuid: string },
  savedIds: SavedIdsByUuid,
) => item.id || savedIds.get(item.uuid);

// What a save sends for an item. One a save has created already goes with
// that id, even if the page hasn't caught up (a save built from an older
// render), so it's updated rather than created again. A new one goes with its
// key, so the response says which id it was given.
export const withSavedId = <T extends { id?: number | null; uuid: string }>(
  item: T,
  savedIds: SavedIdsByUuid,
) => {
  const id = savedIdOf(item, savedIds);
  return id ? { ...item, id } : { ...item, clientId: item.uuid };
};

// After a save, keep what the query can't yet describe: blank criteria (never
// saved) and the keys of criteria created in this session, so their editors
// aren't torn down. Competencies with nothing pending fall back to the query.
// A competency edited while the save was in flight keeps what's on screen
// instead, so the next save sends it.
export const keepBlankCriteriaAfterSave = (
  edits: Record<number, CompetencyCriteriaInputWithUUID[]>,
  // What the save sent: each competency's list as it was when it started.
  sentEdits: Record<number, CompetencyCriteriaInputWithUUID[]>,
  savedCriteriaByCompetencyId: Map<number, CompetencyCriteriaInputWithUUID[]>,
  isBlank: (content?: string | null) => boolean,
  savedIds: SavedIdsByUuid,
) => {
  const next: Record<number, CompetencyCriteriaInputWithUUID[]> = {};
  Object.entries(edits).forEach(([key, criteria]) => {
    const competencyId = Number(key);
    if (criteria !== sentEdits[competencyId]) {
      next[competencyId] = carrySavedIds(criteria, savedIds);
      return;
    }
    const blanks = criteria.filter((item) => isBlank(item.content));
    const hasUnsavedCriteria = criteria.some(
      (item) => !savedIdOf(item, savedIds),
    );
    if (blanks.length === 0 && !hasUnsavedCriteria) {
      // Its criteria come from the query now: ones created in this session
      // keep the key their editor has, so it isn't torn down mid-typing.
      criteria.forEach((item) => {
        const id = savedIdOf(item, savedIds);
        if (id) {
          sessionCriteriaUuids.set(id, item.uuid);
        }
      });
      return;
    }
    // A saved criterion emptied on screen kept its text on the server (see
    // leaveBlankUnsaved): its box stays empty, where it was.
    const blankById = new Map(
      blanks.flatMap((item) => {
        const id = savedIdOf(item, savedIds);
        return id ? [[id, { ...item, id }]] : [];
      }),
    );
    next[competencyId] = [
      ...preserveCriteriaUuids(
        savedCriteriaByCompetencyId.get(competencyId) ?? [],
        criteria,
        savedIds,
      ).map((item) => (item.id && blankById.get(item.id)) || item),
      ...blanks.filter((item) => !savedIdOf(item, savedIds)),
    ];
  });
  return next;
};

// Stable React keys: a saved criterion keeps the same key across refetches,
// so its editor isn't torn down (which would lose focus and flicker). One
// created in this session keeps the random key it was created with.
const sessionCriteriaUuids = new Map<number, string>();
export const criteriaUuid = (id?: number | null) =>
  id ? (sessionCriteriaUuids.get(id) ?? `criteria-${id}`) : uuidv4();

export const roleUuid = (id?: number | null) => (id ? `role-${id}` : uuidv4());

// Carries local keys onto the saved list: criteria (or responsibilities)
// created in this session have a random key, and the saved copy comes back
// with an id instead. Paired by id only (the one a save gave it), never by
// position.
export const preserveCriteriaUuids = <
  T extends { id?: number | null; uuid: string },
>(
  saved: T[],
  local: ReadonlyArray<{ id?: number | null; uuid: string }>,
  savedIds: SavedIdsByUuid,
): T[] => {
  const localUuidById = new Map(
    local.flatMap((item) => {
      const id = savedIdOf(item, savedIds);
      return id ? [[id, item.uuid]] : [];
    }),
  );
  return saved.map((item) => {
    const knownUuid = item.id ? localUuidById.get(item.id) : undefined;
    return knownUuid ? { ...item, uuid: knownUuid } : item;
  });
};

// What's on screen, with the ids saves gave its new items since: nothing else
// changes, so edits made while a save was in flight stand for the next save.
export const carrySavedIds = <T extends { id?: number | null; uuid: string }>(
  current: ReadonlyArray<T>,
  savedIds: SavedIdsByUuid,
): T[] =>
  current.map((item) => {
    const id = savedIdOf(item, savedIds);
    return item.id || !id ? item : { ...item, id };
  });

// Merges a saved list with what's on screen: saved items keep the keys they
// already have, and anything local that isn't saved yet stays put — a blank
// editor, or text typed while the save was in flight.
export const mergeSavedWithLocal = <
  Saved extends { id?: number | null; content?: string | null; uuid: string },
  Local extends { id?: number | null; content?: string | null; uuid: string },
>(
  saved: Saved[],
  local: ReadonlyArray<Local>,
  savedIds: SavedIdsByUuid,
): (Saved | Local)[] => {
  // A saved item emptied on screen kept its text on the server (see
  // leaveBlankUnsaved): it stays empty here, so the box isn't refilled.
  const emptiedById = new Map(
    local.flatMap((item) => {
      const id = savedIdOf(item, savedIds);
      return id && isEmptyValue(item.content) ? [[id, item.content]] : [];
    }),
  );
  const merged = preserveCriteriaUuids(saved, local, savedIds).map((item) =>
    item.id && emptiedById.has(item.id)
      ? { ...item, content: emptiedById.get(item.id) }
      : item,
  );
  const savedUuids = new Set(merged.map((item) => item.uuid));
  return [
    ...merged,
    ...local.filter(
      (item) => !savedIdOf(item, savedIds) && !savedUuids.has(item.uuid),
    ),
  ];
};

// What an autosave sends of a list: new items, and saved ones whose text or
// level changed since they were saved. Items left out stay as they are, and a
// blank one is the box still being typed in (a saved one keeps its text).
export const changedCriteria = <
  T extends {
    id?: number | null;
    content?: string | null;
    level?: number | null;
  },
>(
  items: ReadonlyArray<T>,
  saved: ReadonlyArray<{
    id?: number | null;
    content?: string | null;
    level?: number | null;
  }>,
) => {
  const savedById = new Map(saved.map((item) => [item.id, item]));
  return items.filter((item) => {
    if (isEmptyValue(item.content)) {
      return false;
    }
    const savedItem = item.id ? savedById.get(item.id) : undefined;
    return (
      !savedItem ||
      savedItem.content !== item.content ||
      savedItem.level !== item.level
    );
  });
};

// What a save of a list sends. A blank item is the box still being typed in.
// A new one isn't saved yet. A saved one is sent without its content, so the
// server keeps its text: clearing a box never deletes, only the bin button
// does (see usePendingDeletes).
export const leaveBlankUnsaved = <
  T extends { id?: number | null; content?: string | null },
>(
  items: ReadonlyArray<T>,
) =>
  items
    .filter((item) => item.id || !isEmptyValue(item.content))
    .map((item) => (isEmptyValue(item.content) ? omit(item, "content") : item));

// The saved items a list lost between two versions of it: what a delete
// took out, for the next save to name (see usePendingDeletes).
export const removedIds = (
  before: ReadonlyArray<{ id?: number | null }>,
  after: ReadonlyArray<{ id?: number | null }>,
) => {
  const kept = new Set(after.map((item) => item.id));
  return before
    .map((item) => item.id)
    .filter((id): id is number => !!id && !kept.has(id));
};

export const savedCriteriaByCompetencyId = (
  data?: GetOrganizationCompetenciesQuery,
) =>
  new Map(
    (data ? assertEdgesNonNull(data.competencies) : []).map((competency) => [
      competency.id,
      assertEdgesNonNull(competency.criteria).map((criteria) => ({
        id: criteria.id,
        level: criteria.level,
        content: criteria.content,
        uuid: criteriaUuid(criteria.id),
      })),
    ]),
  );

// Which role types a competency is narrowed to. The model stores two
// independent booleans, but only three of their four combinations mean
// anything: both set reads the same as neither (see get_role_filter on the
// backend), so the form offers one choice of three instead.
export type CompetencyRoleTypeScope = "all" | "management" | "ic";

export const getCompetencyRoleTypeScope = ({
  appliesToManagementRoles,
  appliesToIcRoles,
}: {
  appliesToManagementRoles?: boolean | null;
  appliesToIcRoles?: boolean | null;
}): CompetencyRoleTypeScope => {
  if (appliesToManagementRoles && !appliesToIcRoles) {
    return "management";
  }
  if (appliesToIcRoles && !appliesToManagementRoles) {
    return "ic";
  }
  return "all";
};

export const competencyRoleTypeScopeFlags = (
  scope: CompetencyRoleTypeScope,
) => ({
  appliesToManagementRoles: scope === "management",
  appliesToIcRoles: scope === "ic",
});

// Where a competency is written: for everyone, for a department, or for
// named career tracks. Nobody scopes to a department and tracks at once, so
// the form asks for one of the three rather than two independent lists.
export type CompetencyScopeKind = "everyone" | "department" | "track";

export const getCompetencyScopeKind = ({
  teamCount,
  careerTrackCount,
}: {
  teamCount: number;
  careerTrackCount: number;
}): CompetencyScopeKind =>
  teamCount > 0 ? "department" : careerTrackCount > 0 ? "track" : "everyone";

// A competency narrows to some roles as soon as it is given a role type, a
// department or a career track. Narrowed to none of the three, it applies to
// everyone — so there is nothing to choose between "all roles" and "some
// roles", and the form doesn't ask.
export const getCompetencyAppliesTo = ({
  roleTypeScope,
  teamCount,
  careerTrackCount,
}: {
  roleTypeScope: CompetencyRoleTypeScope;
  teamCount: number;
  careerTrackCount: number;
}) =>
  roleTypeScope === "all" && teamCount === 0 && careerTrackCount === 0
    ? CompetencyAppliesTo.AllRoles
    : CompetencyAppliesTo.SomeRoles;

// What Save sends. A saved competency keeps its value until its role type,
// departments or career tracks are edited: one whose only career track was
// deleted is narrowed to nothing, and working it out again on a rename would
// quietly give it to every role.
export const getCompetencyAppliesToOnSave = ({
  savedAppliesTo,
  isReachChanged,
  ...reach
}: Parameters<typeof getCompetencyAppliesTo>[0] & {
  savedAppliesTo?: CompetencyAppliesTo | null;
  isReachChanged: boolean;
}) =>
  savedAppliesTo && !isReachChanged
    ? savedAppliesTo
    : getCompetencyAppliesTo(reach);

// "Engineering", "Engineering and Design", "Engineering, Design and Support".
const joinTitles = (titles: string[]) =>
  titles.length <= 1
    ? (titles[0] ?? "")
    : `${titles.slice(0, -1).join(", ")} and ${titles[titles.length - 1]}`;

// The selections read back as a sentence, so the reach of a competency doesn't
// have to be inferred from which boxes are filled in. Departments and career
// tracks are additive: a competency can cover a whole department and one track
// outside it.
export const getCompetencyAudienceDescription = ({
  roleTypeScope,
  teamTitles,
  careerTrackTitles,
  label,
}: {
  roleTypeScope: CompetencyRoleTypeScope;
  teamTitles: string[];
  careerTrackTitles: string[];
  label: UseLabelType;
}) => {
  const who =
    roleTypeScope === "management"
      ? "all managers"
      : roleTypeScope === "ic"
        ? "all individual contributors"
        : "everyone";
  const places = [
    teamTitles.length > 0 ? `in ${joinTitles(teamTitles)}` : null,
    careerTrackTitles.length > 0
      ? `on the ${joinTitles(careerTrackTitles)} career ${
          careerTrackTitles.length === 1 ? "track" : "tracks"
        }`
      : null,
  ].filter((part): part is string => part !== null);
  if (places.length === 0) {
    return roleTypeScope === "all"
      ? `Applies to every role in the organization.`
      : `Applies to ${who}, in every ${label("team")} and career track.`;
  }
  return `Applies to ${who} ${places.join(", and ")}.`;
};
